'use client';

import { useEffect, useState } from 'react';

import {
  collection,
  firestore,
  onSnapshot,
  query,
  where,
} from '@ps/functions/firebase/firestore';
import type { CategorizedStore } from '@ps/models/lib/category';
import { CategorizedResourceType } from '@ps/models/lib/category';

/**
 * The creator's store categories, reactively. Ports PopshopRN's
 * `useStoreCategoriesLazily` / `storeCategories$`: reads the
 * `categorizedResources` collection filtered to `resourceType == Store` and
 * `storeId == creatorId`, sorted by `sequenceNo` (the primary category first).
 *
 * Used for the subscribe banner subtitle — RN shows the joined category titles
 * (e.g. "Glamour") there, falling back to the creator's bio when there are none.
 * Returns `null` while loading / on error (callers fall back to bio).
 */
export function useStoreCategories(
  creatorId: string | undefined,
): CategorizedStore[] | null {
  const [categories, setCategories] = useState<CategorizedStore[] | null>(null);

  useEffect(() => {
    if (!creatorId) return;
    const q = query(
      collection(firestore(), 'categorizedResources'),
      where('resourceType', '==', CategorizedResourceType.Store),
      where('storeId', '==', creatorId),
    );
    const unsubscribe = onSnapshot(
      q,
      snap => {
        const rows = snap.docs.map(d => d.data() as CategorizedStore);
        rows.sort((a, b) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0));
        setCategories(rows);
      },
      () => setCategories(null),
    );
    return unsubscribe;
  }, [creatorId]);

  return categories;
}

/** Joined category titles (`"Glamour, Fashion"`), or `undefined` if none. */
export function categoryTitles(
  categories: CategorizedStore[] | null,
): string | undefined {
  if (!categories || categories.length === 0) return undefined;
  return categories.map(c => c.category.title).join(', ');
}
